import { ArrowUpRight, ArrowDownRight, TrendingUp } from "lucide-react"
import { Label, PolarRadiusAxis, RadialBar, RadialBarChart } from "recharts"

import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "./card"
import {
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
} from "./chart"

export const description = "A radial chart showing gross revenue breakdown"

export function ChartRadialStacked({ grossSale, growthGross, totalTax }) {
    // Format currency function for PKR
    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return 'Rs. 0'

        const num = typeof amount === 'string' ? parseFloat(amount.replace(/,/g, '')) : amount

        const formatted = new Intl.NumberFormat('en-PK', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(num)

        return `${formatted}`
    }
    
    const chartData = [{
        metric: "revenue",
        gross: grossSale,
        tax: totalTax
    }]

    const chartConfig = {
        gross: {
            label: "Revenue",
            color: "#017971",
        },
        tax: {
            label: "Tax",
            color: "#3b82f6",
        },
    }

    // Calculate percentage of tax for better visualization
    const taxPercentage = grossSale > 0 ? (totalTax / grossSale) * 100 : 0

    return (
        <Card className="relative overflow-hidden flex flex-col">
            <div className="absolute right-0 top-0 h-16 w-16 bg-green-500/10 rounded-full -translate-y-8 translate-x-8" />
            <CardHeader className="pb-0">
                <div className="flex items-center justify-between w-full">
                    <CardTitle className="text-sm font-medium">Gross Sales</CardTitle>
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                </div>
                <CardDescription>Revenue breakdown without taxes</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-1 items-center pb-0">
                <ChartContainer
                    config={chartConfig}
                    className="mx-auto aspect-square w-full max-w-[250px]">
                    <RadialBarChart
                        data={chartData}
                        endAngle={180}
                        innerRadius={80}
                        outerRadius={130}>
                        <ChartTooltip
                            cursor={false}
                            content={<ChartTooltipContent hideLabel />}
                        />
                        <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
                            <Label
                                content={({ viewBox }) => {
                                    if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                                        return (
                                            <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle">
                                                <tspan
                                                    x={viewBox.cx}
                                                    y={(viewBox.cy || 0) - 16}
                                                    className="fill-foreground text-2xl font-bold"
                                                >
                                                    {formatCurrency(grossSale)}
                                                </tspan>
                                                <tspan
                                                    x={viewBox.cx}
                                                    y={(viewBox.cy || 0) + 4}
                                                    className="fill-muted-foreground"
                                                >
                                                    Gross Revenue
                                                </tspan>
                                            </text>
                                        )
                                    }
                                }}
                            />
                        </PolarRadiusAxis>
                        <RadialBar
                            dataKey="gross"
                            stackId="a"
                            cornerRadius={5}
                            fill="#017971"
                            className="stroke-transparent stroke-2"
                        />
                        <RadialBar
                            dataKey="tax"
                            fill="#3b82f6"
                            stackId="a"
                            cornerRadius={5}
                            className="stroke-transparent stroke-2"
                        />
                    </RadialBarChart>
                </ChartContainer>
            </CardContent>
            <CardFooter className="flex-col gap-2 text-sm pb-6">
                <div className="flex items-center justify-center gap-2 text-sm">
                    {growthGross > 0 ? (
                        <>
                            <ArrowUpRight className="h-4 w-4 text-green-500" />
                            <span className="text-green-500 font-medium">{growthGross.toFixed(1)}%</span>
                        </>
                    ) : (
                        <>
                            <ArrowDownRight className="h-4 w-4 text-red-500" />
                            <span className="text-red-500 font-medium">{Math.abs(growthGross).toFixed(1)}%</span>
                        </>
                    )}
                    <span className="text-muted-foreground">vs yesterday</span>
                </div>
                <div className="text-center text-muted-foreground leading-none">
                    {formatCurrency(totalTax)} taxes • {taxPercentage.toFixed(1)}% of revenue
                </div>
            </CardFooter>
        </Card>
    )
}